/**
 * Tracker de práctica del tutorial (KAN-232, Fase 1 de limpieza).
 *
 * Mientras un módulo del tutorial está activo, el TutorialContext escucha los
 * eventos de repositorio y registra los CREATEs del usuario (animales, gastos,
 * ingresos, lluvias, recordatorios). Al completar el módulo, el hub ofrece el
 * panel "¿cómo seguimos con lo que cargaste?": ítem por ítem, el usuario decide
 * si eran datos reales (se quedan) o de práctica (se borran).
 *
 * Persistencia: localStorage por usuario (mismo estilo que tutorialProgress —
 * funciones puras + read/transform/write con try/catch silencioso).
 *
 * Permiso de borrado: el que borra es el MISMO usuario que creó (por
 * definición, lo que el tracker registra lo creó él durante la corrida), dentro
 * de la ventana de 72 h — ver la migración de policies "borrado propio
 * reciente" y practicaLimpieza.bloqueadoPorVentana.
 */

import { getRepositories } from '../storage';
import { repositoryEvents, localRepositoryEvents } from '../storage/RepositoryChangeEventEmitter';
import type { RepositoryChangeEvent } from '../storage/RepositoryChangeEventEmitter';
import GastoService from '../administration/gastoService';
import IngresoService from '../administration/ingresoService';
import type { Module, Action } from '../../types/permissions';

/** Ventana server-side (RLS) para borrar creaciones propias: 72 h. */
export const VENTANA_BORRADO_PROPIO_HS = 72;

/** Tablas que el tracker escucha. Las 5 primeras tienen user_id (RLS
 * "borrado propio reciente" — ver migración); unidades (campos) y ubicaciones
 * NO: su borrado depende del permiso locations.delete del usuario. */
export type TablaPractica =
  | 'animales'
  | 'gastos'
  | 'ingresos'
  | 'lluvias'
  | 'recordatorios'
  | 'unidades'
  | 'ubicaciones'
  | 'elementos_mapa'
  | 'notas';

/** Tablas con policy "borrado propio reciente": dentro de la ventana de 72 h
 * el propio usuario puede borrarlas aunque su rol no tenga el delete. */
const TABLAS_CON_RLS_PROPIO = new Set<TablaPractica>([
  'animales',
  'gastos',
  'ingresos',
  'lluvias',
  'recordatorios',
]);

export interface ItemPractica {
  tabla: TablaPractica;
  /** Caravana para animales; id (uuid) para el resto. */
  id: string;
  /** Label legible: "Animal 999", "Gasto: Maíz · $50.000". */
  label: string;
  /** ISO timestamp del create (para la ventana de 72 h). */
  creadoEn: string;
}

export interface CorridaPractica {
  moduleId: string;
  iniciadaEn: string;
  items: ItemPractica[];
  /** Decisión tomada en el panel de limpieza (si quedó, no se vuelve a pedir). */
  resuelta?: 'limpiada' | 'conservada';
}

interface PracticaState {
  corridas: Record<string, CorridaPractica>;
}

const key = (userId: string) => `mcd:tutorial:practica:${userId}`;

const getState = (userId: string): PracticaState => {
  try {
    const raw = localStorage.getItem(key(userId));
    if (!raw) return { corridas: {} };
    const parsed = JSON.parse(raw) as PracticaState;
    return parsed?.corridas ? parsed : { corridas: {} };
  } catch {
    return { corridas: {} };
  }
};

const setState = (userId: string, state: PracticaState): void => {
  try {
    localStorage.setItem(key(userId), JSON.stringify(state));
  } catch {
    /* silencioso, como tutorialProgress */
  }
};

/** Reinicia la corrida de un módulo (se llama al EMPEZAR un módulo desde cero). */
export const iniciarCorrida = (userId: string, moduleId: string): void => {
  const state = getState(userId);
  state.corridas[moduleId] = { moduleId, iniciadaEn: new Date().toISOString(), items: [] };
  setState(userId, state);
};

/** Crea la corrida si no existe (retomar un módulo no debe borrar lo registrado). */
export const asegurarCorrida = (userId: string, moduleId: string): void => {
  const state = getState(userId);
  if (!state.corridas[moduleId]) {
    state.corridas[moduleId] = { moduleId, iniciadaEn: new Date().toISOString(), items: [] };
    setState(userId, state);
  }
};

/** Registra un create (dedupe por tabla+id, tope de 50 ítems por corrida). */
export const registrarCreate = (userId: string, moduleId: string, item: ItemPractica): void => {
  const state = getState(userId);
  const corrida = state.corridas[moduleId];
  if (!corrida) return;
  if (corrida.items.some(i => i.tabla === item.tabla && i.id === item.id)) return;
  if (corrida.items.length >= 50) return;
  corrida.items.push(item);
  setState(userId, state);
};

export const obtenerCorrida = (userId: string, moduleId: string): CorridaPractica | null =>
  getState(userId).corridas[moduleId] ?? null;

/** Quita ítems de la corrida (tras borrarlos con éxito en el panel). */
export const quitarItems = (userId: string, moduleId: string, ids: ItemPractica[]): void => {
  const state = getState(userId);
  const corrida = state.corridas[moduleId];
  if (!corrida) return;
  const fuera = new Set(ids.map(i => `${i.tabla}|${i.id}`));
  corrida.items = corrida.items.filter(i => !fuera.has(`${i.tabla}|${i.id}`));
  setState(userId, state);
};

export const marcarResuelta = (userId: string, moduleId: string, resolucion: 'limpiada' | 'conservada'): void => {
  const state = getState(userId);
  const corrida = state.corridas[moduleId];
  if (!corrida) return;
  corrida.resuelta = resolucion;
  setState(userId, state);
};

/** Corridas con ítems sin decidir (para la sección "práctica pendiente" del hub). */
export const corridasPendientes = (userId: string): CorridaPractica[] =>
  Object.values(getState(userId).corridas).filter(c => c.items.length > 0 && !c.resuelta);

/**
 * Mapea un evento de repositorio a un ítem de práctica. Sólo INSERTs de las
 * tablas tracked; el record de animales (emitter local) trae la caravana, el
 * de gastos/ingresos el concepto+monto, etc. Devuelve null si no aplica.
 *
 * OJO con los emitters: para "animal nuevo" el INSERT de 'animales' viaja en
 * localRepositoryEvents (AnimalRepository.emitLocalInsert); el INSERT de
 * 'animales_historico' de repositoryEvents NO es un animal nuevo (también
 * viaja en ediciones y borrados — event sourcing).
 */
export const itemPracticaDesdeEvento = (event: RepositoryChangeEvent): ItemPractica | null => {
  if (event.action !== 'INSERT') return null;
  const record = (event.record ?? {}) as Record<string, unknown>;
  const creadoEn = new Date().toISOString();

  switch (event.table) {
    case 'animales': {
      // Sólo el emitter local emite INSERT de 'animales' para animales NUEVOS
      // (las ediciones usan UPDATE). Filtramos eventos con caravana.
      const caravana = typeof record.caravana === 'string' ? record.caravana : null;
      if (!caravana || !event.localOnly) return null;
      return { tabla: 'animales', id: caravana, label: `Animal ${caravana}`, creadoEn };
    }
    case 'gastos':
    case 'ingresos': {
      const concepto = typeof record.concepto === 'string' ? record.concepto : 'Sin concepto';
      const monto = typeof record.monto === 'number' ? record.monto : null;
      const montoTxt = monto !== null ? ` · $${monto.toLocaleString('es-AR')}` : '';
      const prefijo = event.table === 'gastos' ? 'Gasto' : 'Ingreso';
      return {
        tabla: event.table,
        id: String(record.id ?? ''),
        label: `${prefijo}: ${concepto}${montoTxt}`,
        creadoEn,
      };
    }
    case 'lluvias': {
      const mm = typeof record.milimetros === 'number' ? record.milimetros : null;
      const fecha = typeof record.fecha === 'string' ? record.fecha : '';
      if (mm === null) return null;
      return {
        tabla: 'lluvias',
        id: String(record.id ?? ''),
        label: `Lluvia: ${mm} mm${fecha ? ` (${fecha})` : ''}`,
        creadoEn,
      };
    }
    case 'recordatorios': {
      const titulo = typeof record.title === 'string' ? record.title : null;
      if (!titulo) return null;
      return { tabla: 'recordatorios', id: String(record.id ?? ''), label: `Recordatorio: ${titulo}`, creadoEn };
    }
    case 'unidades': {
      // Campos (unified: los campos se crean por repos.unidad → tabla 'unidades')
      const nombre = typeof record.nombre === 'string' ? record.nombre : null;
      if (!nombre) return null;
      return { tabla: 'unidades', id: String(record.id ?? ''), label: `Campo: ${nombre}`, creadoEn };
    }
    case 'ubicaciones': {
      const nombre = typeof record.nombre === 'string' ? record.nombre : null;
      if (!nombre) return null;
      return { tabla: 'ubicaciones', id: String(record.id ?? ''), label: `Ubicación: ${nombre}`, creadoEn };
    }
    case 'elementos_mapa': {
      // Elementos dibujados en el mapa del tutorial del mapa: cuadros,
      // corrales, aguadas, puestos, anotaciones…
      const nombre = typeof record.nombre === 'string' ? record.nombre : null;
      const tipoRaw = typeof record.tipo === 'string' ? record.tipo : '';
      if (!nombre && !tipoRaw) return null;
      const TIPOS: Record<string, string> = {
        CUADRO: 'Cuadro',
        CORRAL: 'Corral',
        AGUADA: 'Aguada',
        PUESTO: 'Puesto',
        ANOTACION: 'Anotación',
      };
      const tipoLabel = TIPOS[tipoRaw] ?? (tipoRaw ? tipoRaw.toLowerCase() : 'Elemento');
      return {
        tabla: 'elementos_mapa',
        id: String(record.id ?? ''),
        label: `${tipoLabel}${nombre ? `: ${nombre}` : ''}`,
        creadoEn,
      };
    }
    case 'notas': {
      const titulo = typeof record.title === 'string' ? record.title : null;
      if (!titulo) return null;
      return { tabla: 'notas', id: String(record.id ?? ''), label: `Nota: ${titulo}`, creadoEn };
    }
    default:
      return null;
  }
};

/**
 * Borra un ítem por la MISMA vía que usa la UI de cada página (soft-delete
 * donde corresponde, evento BORRADO para animales — event sourcing).
 */
export const borrarItemPractica = async (item: ItemPractica): Promise<void> => {
  switch (item.tabla) {
    case 'animales':
      // delete lanza si no encontró el animal (antes era no-op silencioso):
      // la falla se hace visible en el panel del modal.
      await getRepositories().animal.delete(item.id);
      break;
    case 'gastos':
      await GastoService.delete(item.id);
      break;
    case 'ingresos':
      await IngresoService.delete(item.id);
      break;
    case 'lluvias':
      await getRepositories().lluvia.delete(item.id);
      break;
    case 'recordatorios':
      await getRepositories().recordatorio.delete(item.id);
      break;
    case 'unidades':
      await getRepositories().unidad.delete(item.id);
      break;
    case 'ubicaciones':
      await getRepositories().ubicacion.delete(item.id);
      break;
    case 'elementos_mapa':
      await getRepositories().elementoMapa.delete(item.id);
      break;
    case 'notas':
      await getRepositories().nota.delete(item.id);
      break;
  }
};

/** Permiso que gatea el borrado "normal" (sin ventana) de cada tabla. */
export const permisoBorrado = (tabla: TablaPractica): { module: Module; action: Action } => {
  switch (tabla) {
    case 'animales':
      return { module: 'animals_history', action: 'delete' };
    case 'gastos':
    case 'ingresos':
      return { module: 'financial', action: 'delete' };
    case 'lluvias':
    case 'recordatorios':
      return { module: 'tools', action: 'delete' };
    case 'unidades':
    case 'ubicaciones':
      return { module: 'locations', action: 'delete' };
    case 'elementos_mapa':
      return { module: 'map', action: 'delete' };
    case 'notas':
      return { module: 'tools', action: 'delete' };
  }
};

/**
 * Candado del panel: un ítem no se puede borrar si el usuario no tiene el
 * permiso del módulo Y (la tabla no tiene policy propio-reciente O la ventana
 * de 72 h ya pasó — la RLS lo rechazaría igual).
 */
export const itemBloqueado = (
  item: ItemPractica,
  puede: (permiso: { module: Module; action: Action }) => boolean,
  ahora = Date.now(),
): boolean => {
  if (puede(permisoBorrado(item.tabla))) return false;
  if (!TABLAS_CON_RLS_PROPIO.has(item.tabla)) return true;
  return fueraDeVentana(item, ahora);
};

/**
 * True si el ítem está fuera de la ventana de 72 h (y el usuario no tiene el
 * permiso de borrado del módulo): se muestra candado — la policy RLS lo
 * rechazaría igual.
 */
export const fueraDeVentana = (item: ItemPractica, ahora = Date.now()): boolean =>
  ahora - new Date(item.creadoEn).getTime() > VENTANA_BORRADO_PROPIO_HS * 60 * 60 * 1000;

/** Suscribe el tracker a los DOS emitters (sync + local). Devuelve unsubscribe. */
export const suscribirTracker = (
  onEvento: (item: ItemPractica) => void,
): (() => void) => {
  const listener = (event: RepositoryChangeEvent) => {
    const item = itemPracticaDesdeEvento(event);
    if (item && item.id) onEvento(item);
  };
  const offSync = repositoryEvents.subscribe(listener);
  const offLocal = localRepositoryEvents.subscribe(listener);
  return () => {
    offSync();
    offLocal();
  };
};
